import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import HistoryListItem from './HistoryListItem';
import SideDialog from '../../../../common/SideDialog';
import HistoryListTitle from './HistoryListTitle';
import {
  HistoryGroupDate,
  HistoryGroupSeparator,
  Content,
  ContentStyle,
  Footer,
} from './ParticipantHistoryDialog.styled';
import NoResults from '../../NoResults';
import InfiniteTable from '../../../../common/InfiniteTable/InfiniteTable';
import TextButton from '../../../../common/Buttons/TextButton';
import Accordion from '../../../../common/Accordion/Accordion';

const renderEventHistory = (historyEventData, eventDate) => {
  const isEducator = (role) => role !== 'student';
  return (
    <div key={eventDate}>
      <HistoryGroupDate>{eventDate}</HistoryGroupDate>
      {Object.keys(historyEventData[eventDate]).map((eventKey) =>
        // 3 and above related data are rendered as accordions
        historyEventData[eventDate][eventKey].data.length >= 3 ? (
          <Accordion
            showDefaultExpandIcon
            key={`${eventDate}-${eventKey}`}
            dataSet={historyEventData[eventDate][eventKey].data}
            AccordionTitleComponent={() => (
              <HistoryListTitle
                user={historyEventData[eventDate][eventKey].user}
                count={historyEventData[eventDate][eventKey].data.length}
                eventType={historyEventData[eventDate][eventKey].eventType}
              />
            )}
            renderAccordionItem={({ key, participant, role }) => (
              <HistoryListItem
                size="small"
                isEducator={isEducator(role)}
                key={key}
                user={historyEventData[eventDate][eventKey].user}
                eventType={historyEventData[eventDate][eventKey].eventType}
                participant={`${participant.firstName} ${participant.lastName}`}
              />
            )}
          />
        ) : (
          historyEventData[eventDate][
            eventKey
          ].data.map(({ key, participant, role, eventType }) => (
            <HistoryListItem
              key={key}
              participant={`${participant.firstName} ${participant.lastName}`}
              user={historyEventData[eventDate][eventKey].user}
              eventType={eventType}
              isEducator={isEducator(role)}
            />
          ))
        )
      )}
      <HistoryGroupSeparator />
    </div>
  );
};

const ParticipantHistoryDialog = ({
  open,
  onClose,
  spaceName,
  isFetching,
  lastEventDate,
  hasFetchedAll,
  historyEventDataCount,
  participantsHistory,
  fetchParticipantsHistory,
}) => {
  const [isRefetching, setIsRefetching] = useState(false);

  const refetchHistoryData = async () => {
    setIsRefetching(true);
    await fetchParticipantsHistory({ reset: true });
    setIsRefetching(false);
  };

  useEffect(() => {
    if (!hasFetchedAll && !isFetching && historyEventDataCount === 0) {
      fetchParticipantsHistory();
    }
  });

  useEffect(() => {
    if (open) {
      refetchHistoryData();
    }
  }, [open]);

  const fetchMore = () => {
    if (!isFetching && !hasFetchedAll) {
      fetchParticipantsHistory({ before: lastEventDate });
    }
  };

  return (
    <SideDialog
      title="Participants history"
      subtitle={spaceName}
      open={open}
      onClose={onClose}
      width="640px"
    >
      <Content>
        <InfiniteTable
          contentStyle={ContentStyle}
          items={Object.keys(participantsHistory)}
          keyExtractor={(eventDate) => eventDate}
          renderItem={(eventDate) =>
            renderEventHistory(participantsHistory, eventDate)
          }
          renderEmptyList={() =>
            historyEventDataCount === 0 && !isFetching && !isRefetching ? (
              <NoResults primaryText="There aren't any new records of participants being added to this space." />
            ) : null
          }
          renderFooter={() =>
            !isFetching && (
              <Footer>
                <TextButton label="Load more" onClick={fetchMore} />
              </Footer>
            )
          }
          loading={isRefetching || isFetching}
          hasNext={!hasFetchedAll}
          loadNext={fetchMore}
          resetScrollOnMount
        />
      </Content>
    </SideDialog>
  );
};

ParticipantHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  spaceName: PropTypes.string.isRequired,
  isFetching: PropTypes.bool.isRequired,
  hasFetchedAll: PropTypes.bool.isRequired,
  lastEventDate: PropTypes.string.isRequired,
  historyEventDataCount: PropTypes.number.isRequired,
  fetchParticipantsHistory: PropTypes.func.isRequired,
  participantsHistory: PropTypes.shape({}).isRequired,
};

export default ParticipantHistoryDialog;
